/**
 * @file field-links.ts
 * @description 表單欄位宣告式連動的純函式（2026-09-25 回報序66/69）。
 *
 * 1. 勾選即複製（CheckboxCopyRule）：「與戶籍地址相同」勾選 → 通訊地址 = 戶籍地址並鎖定。
 *    - 顯式宣告：checkbox 欄位的 `copy: { from, to }`；
 *    - 慣例推導（讓**所有表單**不改 schema 即生效，含既有歷史版本）：checkbox id 形如
 *      `<a>_same_as_<b>`（如 mailing_same_as_registered），且表單內存在 `<b>_address` 與
 *      `<a>_address`（或 `<b>` 與 `<a>`）欄位時，自動視為 `{ from: <b>…, to: <a>… }`。
 * 2. 條件顯示（showWhen）：欄位只在指定欄位等於某值時顯示。
 *
 * 皆為純函式，Renderer（前端）與 API 預填（後端）共用。
 */
/** `<a>_same_as_<b>` 慣例 id 解析 */
const SAME_AS_RE = /^([a-z0-9_]+?)_same_as_([a-z0-9_]+)$/i;
/**
 * 掃描 schema，收集全部「勾選即複製」規則（顯式宣告優先，其次慣例推導）。
 * 來源或目標欄位不存在的規則一律丟棄（避免寫入 schema 外的鍵）。
 */
export function collectCopyRules(schema) {
    const fieldIds = new Set();
    const checkboxes = [];
    for (const section of schema.sections ?? []) {
        for (const f of section.fields) {
            fieldIds.add(f.id);
            if (f.type === 'checkbox')
                checkboxes.push(f);
        }
    }
    const rules = [];
    for (const cb of checkboxes) {
        if (cb.copy) {
            if (fieldIds.has(cb.copy.from) && fieldIds.has(cb.copy.to)) {
                rules.push({ checkbox: cb.id, from: cb.copy.from, to: cb.copy.to });
            }
            continue;
        }
        const m = SAME_AS_RE.exec(cb.id);
        if (!m)
            continue;
        const [, target, source] = m;
        const candidates = [
            [`${source}_address`, `${target}_address`],
            [source, target],
        ];
        const hit = candidates.find(([from, to]) => fieldIds.has(from) && fieldIds.has(to));
        if (hit)
            rules.push({ checkbox: cb.id, from: hit[0], to: hit[1] });
    }
    return rules;
}
/** 值是否為「空」（undefined / null / 空白字串）：來源為空時不覆寫目標，避免誤清已填地址 */
function isEmptyCopyValue(v) {
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}
/**
 * 套用複製規則：checkbox 為 true 的規則把 from 值寫入 to。
 *
 * - 傳入 `changedId`（本次被編輯的欄位 id）時，只套用「checkbox 或 from 恰為該欄位」的規則，
 *   其餘欄位的編輯不會觸發複製（避免每次輸入都靜默覆寫目標欄位）；
 *   省略 `changedId` 時（如初始化 / 後端預填）套用全部規則。
 * - 來源值為空（undefined / null / 空白字串）時**不覆寫**目標，保留使用者既有輸入。
 *
 * 無任何變化時回傳原物件（引用不變，便於 React bail-out）。
 *
 * @param rules 由 collectCopyRules 收集的規則
 * @param data 當前表單資料
 * @param changedId 本次變更的欄位 id（可選）
 */
export function applyCopyRules(rules, data, changedId) {
    let out = data;
    for (const r of rules) {
        if (changedId !== undefined && changedId !== r.checkbox && changedId !== r.from)
            continue;
        if (out[r.checkbox] !== true)
            continue;
        const next = out[r.from];
        if (isEmptyCopyValue(next))
            continue;
        if (out[r.to] === next)
            continue;
        if (out === data)
            out = { ...data };
        out[r.to] = next;
    }
    return out;
}
/** 當前被複製規則鎖定（checkbox 已勾）的目標欄位 id 集合 */
export function lockedFieldIds(rules, data) {
    const out = new Set();
    for (const r of rules)
        if (data[r.checkbox] === true)
            out.add(r.to);
    return out;
}
/**
 * 建立 schema 內「欄位 id → 欄位定義」對照表，供 isFieldVisible 做遞移（祖先）可見性判斷。
 *
 * @param schema 表單 schema
 * @returns id → FormField 的 Map
 */
export function buildFieldMap(schema) {
    const map = new Map();
    for (const section of schema.sections ?? []) {
        for (const f of section.fields)
            map.set(f.id, f);
    }
    return map;
}
/**
 * 欄位條件顯示判斷：無 showWhen → 顯示；
 * 依據欄位值為陣列（multiselect）時「包含」即成立，否則嚴格相等。
 *
 * 傳入 `fieldMap` 時可見性為**遞移**的：若 showWhen 依據欄位本身被隱藏，則本欄位也隱藏
 * （A 隱藏 → 依 A 顯示的 B 隱藏 → 依 B 顯示的 C 也隱藏）。以 visited 集合防循環依賴，
 * 遇到環視為隱藏（環內任何條件都無法穩定成立）。
 * 未傳 `fieldMap` 時只判斷本欄位自身條件（相容舊呼叫端）。
 *
 * @param field 欲判斷的欄位
 * @param data 當前表單資料
 * @param fieldMap buildFieldMap(schema) 的結果（可選）
 */
export function isFieldVisible(field, data, fieldMap) {
    const visited = new Set();
    let cur = field;
    while (cur) {
        const cond = cur.showWhen;
        if (!cond)
            return true;
        if (visited.has(cur.id))
            return false;
        visited.add(cur.id);
        const v = data[cond.field];
        const ok = Array.isArray(v) ? v.includes(cond.equals) : v === cond.equals;
        if (!ok)
            return false;
        // 依據欄位不在 schema 內（或未提供對照表）→ 無祖先可檢查，視為顯示
        cur = fieldMap?.get(cond.field);
    }
    return true;
}
//# sourceMappingURL=field-links.js.map